import { motion } from "framer-motion";
import { Trophy, PartyPopper, CakeSlice } from "lucide-react";

const CLASSES = [
  { name: "Roque", pct: 100 },
  { name: "Fallon-Mcknight", pct: 100 },
  { name: "Gorban-Polensvaig", pct: 100 },
  { name: "Landman", pct: 100 },
  { name: "Oliva", pct: 96 },
  { name: "Teshin-Anderson", pct: 96 },
  { name: "Harris", pct: 95 },
  { name: "Nickmeyer", pct: 95 },
  { name: "Tiffany", pct: 94 },
  { name: "Fittoria", pct: 92 },
  { name: "Sheffield", pct: 92 },
  { name: "Farfan-Vega", pct: 91 },
  { name: "Boswell", pct: 90 },
  { name: "Peddycord", pct: 88 },
  { name: "Meneses", pct: 82 },
  { name: "Sweat", pct: 78 },
  { name: "Longo", pct: 75 },
  { name: "Ormsby", pct: 52 },
];

const barColor = (pct) => {
  if (pct >= 100) return { from: "#FFE600", to: "#F59E0B", glow: "rgba(255,230,0,0.45)" };
  if (pct >= 90) return { from: "#00F5D4", to: "#00F0FF", glow: "rgba(0,240,255,0.4)" };
  if (pct >= 80) return { from: "#38BDF8", to: "#00F0FF", glow: "rgba(56,189,248,0.35)" };
  if (pct >= 70) return { from: "#B500FF", to: "#D946EF", glow: "rgba(181,0,255,0.35)" };
  if (pct >= 60) return { from: "#FF007F", to: "#D946EF", glow: "rgba(255,0,127,0.35)" };
  return { from: "#FDA4AF", to: "#FF007F", glow: "rgba(253,164,175,0.35)" };
};

const Row = ({ cls, index }) => {
  const c = barColor(cls.pct);
  const winner = cls.pct >= 90;
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      data-testid={`class-row-${cls.name.toLowerCase()}`}
      className="py-1.5"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1.5 text-sm font-bold text-slate-900 notranslate">
          {cls.pct >= 100 ? (
            <Trophy size={14} className="text-yellow-500" />
          ) : winner ? (
            <PartyPopper size={14} className="text-cyan-500" />
          ) : null}
          {cls.name}
        </span>
        <span
          className={`text-xs font-black tabular-nums px-2 py-0.5 rounded-full ${
            winner ? "bg-slate-900 text-cyan-300" : "bg-slate-100 text-slate-600"
          }`}
        >
          {cls.pct}%
        </span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${cls.pct}%` }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 1, delay: 0.15 + index * 0.04, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${c.from}, ${c.to})`,
            boxShadow: `0 0 12px ${c.glow}`,
          }}
        />
      </div>
    </motion.div>
  );
};

export const ClassChallenge = () => (
  <section id="challenge" data-testid="class-challenge-section" className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-[#FAFAFA]">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tight text-slate-900 uppercase">
          Class Membership <span className="neon-cyan-shadow">Challenge</span>
        </h2>
        <p className="mt-4 max-w-2xl text-base md:text-lg font-medium text-slate-600 leading-relaxed">
          Get your class to <strong className="text-slate-900">90% membership participation</strong> and
          the PTA throws your class a{" "}
          <span className="inline-flex items-center gap-1 font-bold text-pink-500">
            <CakeSlice size={16} /> FREE sweet treat party!
          </span>{" "}
          Every join counts — help your class over the line before it ends.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
        data-testid="class-challenge-chart"
        className="mt-10 relative bg-white rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.05)] p-6 sm:p-8"
      >
        <div className="relative">
          <div
            className="absolute top-0 bottom-0 border-l-2 border-dashed border-pink-500 z-10 pointer-events-none"
            style={{ left: "90%" }}
          >
            <span className="absolute -top-1 -translate-x-1/2 bg-pink-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap shadow-[0_0_12px_rgba(255,0,127,0.5)]">
              90% Goal
            </span>
          </div>
          <div className="pt-6">
            {CLASSES.map((cls, i) => (
              <Row key={cls.name} cls={cls} index={i} />
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <Trophy size={14} className="text-yellow-500" /> 100% Club
            <span className="text-slate-300">·</span>
            <PartyPopper size={14} className="text-cyan-500" /> Party earned (90%+)
          </p>
          <a
            href="#tiers"
            data-testid="challenge-join-button"
            className="px-7 py-3 rounded-full bg-slate-900 text-cyan-300 text-xs font-bold uppercase tracking-widest hover:shadow-[0_0_18px_rgba(0,240,255,0.4)] transition-shadow"
          >
            Push your class over 90% — Join Now
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);
